import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

import Iconify from '@/components/media/iconify';
import { ShowIf } from '@/utils/ui/showIf';
import { useOdooLink } from '@/hooks/integrations/use-odoo-link';
import { OdooSearchAutocomplete } from './odoo-search-autocomplete';
import { isOdooProductItem } from '@/types/odoo.types';
import type { OdooResourceType, OdooSearchItem, ManualLinkResult } from '@/types/odoo.types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface OdooLinkDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Close handler. */
  onClose: () => void;
  /** The entity type to link. */
  resourceType: OdooResourceType;
  /** The Hub resource MongoDB ID. */
  resourceId: string;
  /** Display name of the Hub resource. */
  resourceName: string;
  /** Called after a successful link. */
  onSuccess?: (result: ManualLinkResult) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Readable label for a resource type. */
const RESOURCE_TYPE_LABELS: Record<OdooResourceType, string> = {
  organizations: 'Organization',
  'legal-entities': 'Legal Entity',
  contacts: 'Contact',
  plans: 'Plan',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OdooLinkDialog({
  open,
  onClose,
  resourceType,
  resourceId,
  resourceName,
  onSuccess,
}: OdooLinkDialogProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<OdooSearchItem | null>(null);

  const { link, isLoading } = useOdooLink({
    resourceType,
    onSuccess: (result) => {
      onSuccess?.(result);
      // Force-close without checking isLoading — mutation already succeeded
      setSelected(null);
      onClose();
    },
  });

  const handleClose = () => {
    if (!isLoading) {
      setSelected(null);
      onClose();
    }
  };

  const handleLink = () => {
    if (!selected) return;
    link({ resourceId, odooId: selected.id });
  };

  const typeLabel = t(RESOURCE_TYPE_LABELS[resourceType]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify icon="mdi:link" width={24} />
        {t('Link with Odoo')}
      </DialogTitle>

      <DialogContent>
        {/* Hub resource info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('Hub Resource')}
          </Typography>
          <Typography variant="body1">
            <strong>{typeLabel}:</strong> {resourceName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {resourceId}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Odoo search */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('Search Odoo resource to link')}
        </Typography>

        <OdooSearchAutocomplete
          resourceType={resourceType}
          onSelect={setSelected}
          label={t('Search in Odoo')}
          disabled={isLoading}
        />

        {/* Preview of selected Odoo resource */}
        <ShowIf condition={selected !== null}>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('Selected Odoo resource')}
            </Typography>
            <Typography variant="body2">
              <strong>{t('Name')}:</strong> {selected?.name}
            </Typography>
            <Typography variant="body2">
              <strong>Odoo ID:</strong> {selected?.id}
            </Typography>
            {selected && isOdooProductItem(selected) && (
              <Typography variant="body2">
                <strong>SKU:</strong> {selected.default_code || '—'}
              </Typography>
            )}
            {selected && !isOdooProductItem(selected) && typeof selected.email === 'string' && (
              <Typography variant="body2">
                <strong>Email:</strong> {selected.email}
              </Typography>
            )}
          </Box>
        </ShowIf>

        {/* Info alert */}
        <Alert severity="info" sx={{ mt: 2 }}>
          {t(
            'This will associate the Hub resource with the selected Odoo record. The Odoo ID will be stored in the Hub resource.'
          )}
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose} disabled={isLoading}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleLink}
          disabled={!selected || isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Iconify icon="mdi:link" width={18} />
            )
          }
        >
          {isLoading ? t('Linking...') : t('Link')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
